import type Phaser from 'phaser';

/** Palette whites and greys (data/assets/palette.gpl): a cloud's core and its rim. */
const CORE = 0xf8f2ea;
const RIM = 0xa9a9b8;
const PUFF_MS = 520;
/** Clouds spread around the feet, flattened: dust kicked along the ground, not a blast. */
const CLOUDS = 9;

/**
 * The puff of dust when a rider gets on or off their mount.
 *
 * Drawn from code rather than a sprite sheet: a handful of round pixel clouds
 * that swell, drift outward and fade. Every pixel is a whole 1×1 rectangle
 * at a rounded position (CLAUDE.md rule 6), redrawn as the radius grows. A
 * purely visual effect: the server already decided the rider got on.
 */
export function puffSmoke(
  scene: Phaser.Scene,
  feet: { x: number; y: number },
  depth: number,
): void {
  for (let index = 0; index < CLOUDS; index += 1) {
    const angle = (index / CLOUDS) * Math.PI * 2 + Math.random() * 0.5;
    const reach = 7 + Math.random() * 6;
    const start = { x: feet.x + Math.cos(angle) * 3, y: feet.y - 3 + Math.sin(angle) * 1.5 };
    const state = { x: start.x, y: start.y, radius: 1 };
    const cloud = scene.add.graphics().setDepth(depth);
    const maxRadius = 2 + Math.round(Math.random());
    scene.tweens.add({
      targets: state,
      x: start.x + Math.cos(angle) * reach,
      // Half the reach downward, and a lift: the dust rises as it spreads.
      y: start.y + Math.sin(angle) * reach * 0.5 - (3 + Math.random() * 4),
      radius: maxRadius,
      duration: PUFF_MS * (0.7 + Math.random() * 0.3),
      delay: Math.random() * 60,
      ease: 'Quad.easeOut',
      onUpdate: (tween) => {
        drawCloud(cloud, Math.round(state.radius));
        cloud.setPosition(Math.round(state.x), Math.round(state.y));
        cloud.setAlpha(tween.progress < 0.4 ? 1 : 1 - (tween.progress - 0.4) / 0.6);
      },
      onComplete: () => {
        cloud.destroy();
      },
    });
  }
}

/** A round blob of pixels, lighter inside, grey at the edge. */
function drawCloud(graphics: Phaser.GameObjects.Graphics, radius: number): void {
  graphics.clear();
  const outer = radius * radius + radius;
  const inner = (radius - 1) * (radius - 1) + radius - 1;
  for (let dy = -radius; dy <= radius; dy += 1) {
    for (let dx = -radius; dx <= radius; dx += 1) {
      const distance = dx * dx + dy * dy;
      if (distance > outer) continue;
      graphics.fillStyle(radius > 1 && distance > inner ? RIM : CORE, 1);
      graphics.fillRect(dx, dy, 1, 1);
    }
  }
}
